import { isPlatformBrowser } from "@angular/common";
import {
  HttpEvent,
  HttpHandlerFn,
  HttpHeaders,
  HttpRequest,
} from "@angular/common/http";
import { inject, PLATFORM_ID } from "@angular/core";
import { from, Observable, switchMap } from "rxjs";

/**
 * WAF-opacity request envelope, applied in the HTTP layer.
 *
 * Azure WAF (OWASP CRS) false-positives on legitimate rich content and caps the
 * request body at ~128 KB. The team cannot change the WAF policy, so mutating
 * /api payloads are made opaque and small, and go-cxa's BodyDecode middleware
 * reverses it server-side. See `public/waf-envelope-sw.js` for the long-form
 * rationale — this file deliberately produces the byte-identical envelope.
 *
 * **Why this exists as well as the Service Worker.** The worker was the only
 * thing applying the envelope, so any page it did not control sent plaintext
 * and got blocked by the WAF. That is not a rare edge:
 *
 *  - Safari Private Browsing refuses to register a Service Worker at all.
 *  - Safari's ITP deletes Service Worker registrations after 7 days without a
 *    visit, so an occasional user is a first-time user every time.
 *  - A hard reload (Cmd+Shift+R) loads the document bypassing the worker, and
 *    because `clients.claim()` only runs on `activate`, that page stays
 *    uncontrolled for its whole lifetime.
 *  - iOS in-app browsers (Line, Teams) may not run workers.
 *
 * An interceptor has none of those failure modes: it is part of the bundle, so
 * it runs on the first request of the first load, in every browser and every
 * privacy mode. Every mutating /api call in this app goes through HttpClient,
 * so this covers all of them.
 *
 * The worker stays registered and keeps two jobs. It still encodes anything
 * that ever bypasses HttpClient, and — because encoding here means DevTools
 * now shows an opaque octet-stream where it used to show the plain body — it
 * decodes a copy of what this interceptor sent and logs the plaintext, so the
 * payload is still readable while debugging. Neither job double-encodes: the
 * worker's first guard sees the `X-Enc` / `X-Enc-Form` that everything from
 * here carries and takes the decode path instead.
 *
 * That readable log is the one thing a browser without a working Service
 * Worker gives up. The request itself still goes out correctly encoded, which
 * is the whole point of moving the envelope here.
 */

const ENCODE_PATH = /^\/api\//;
const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** gzip is optional and signalled per-request via X-Enc-Zip, so a browser
 *  without CompressionStream (Safari < 16.4) simply sends base64url only. */
function canGzip(): boolean {
  return typeof CompressionStream !== "undefined";
}

/** URL-safe base64 without padding — matches go-cxa's base64.RawURLEncoding.
 *  NB: standard base64 (+ / =) trips Azure WAF rule 941120, do not change it.
 *  Chunked so a large upload cannot overflow the argument list. */
function base64urlFromBytes(bytes: Uint8Array<ArrayBuffer>): string {
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCodePoint.apply(
      null,
      bytes.subarray(i, i + CHUNK) as unknown as number[],
    );
  }
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
}

/** Writer idiom rather than `new Response(bytes).body` so an empty input works
 *  too — a zero-length Uint8Array has a null `.body` in browsers. */
async function gzipBytes(
  bytes: Uint8Array<ArrayBuffer>,
): Promise<Uint8Array<ArrayBuffer>> {
  const cs = new CompressionStream("gzip");
  const compressed = new Response(cs.readable).arrayBuffer();
  const writer = cs.writable.getWriter();
  await writer.write(bytes);
  await writer.close();
  return new Uint8Array(await compressed);
}

async function encodeBytes(
  bytes: Uint8Array<ArrayBuffer>,
  zip: boolean,
): Promise<string> {
  return base64urlFromBytes(zip ? await gzipBytes(bytes) : bytes);
}

/**
 * Splits every run of dashes with a newline, for values the WAF inspects as
 * request ARGS — i.e. multipart text fields.
 *
 * base64url has `-` in its alphabet, and CRS 942440 ("SQL comment sequence")
 * matches `--[^-]*?-`. Two dashes land side by side about once per 4,000
 * characters, so a banner `payLoad` was blocked or not depending on its own
 * bytes (production, 2026-09-17). That rule scores 5, which meets the blocking
 * threshold on its own.
 *
 * go-cxa decodes with base64.RawURLEncoding, and Go's decoder skips \r and \n,
 * so the separator is invisible to the server and ships without a backend
 * release. It is the only character with that property — anything else makes
 * the value undecodable.
 *
 * Not applied to the octet-stream body: the WAF does not parse that as ARGS,
 * and no rule has ever matched it.
 */
export function breakDashRuns(encoded: string): string {
  return encoded.replaceAll(/-(?=-)/g, "-\n");
}

async function encodeText(value: string, zip: boolean): Promise<string> {
  return breakDashRuns(await encodeBytes(new TextEncoder().encode(value), zip));
}

/** Same-origin check that tolerates both relative ("/api/x") and absolute
 *  request URLs. Anything we cannot parse is left alone. */
function isSameOriginApiUrl(url: string): boolean {
  try {
    const base =
      typeof document !== "undefined" ? document.baseURI : location.origin;
    const parsed = new URL(url, base);
    return (
      parsed.origin === location.origin && ENCODE_PATH.test(parsed.pathname)
    );
  } catch {
    return false;
  }
}

function hasEnvelope(headers: HttpHeaders): boolean {
  return headers.has("X-Enc") || headers.has("X-Enc-Form");
}

/** JSON/text: gzip+base64url the whole body into an opaque octet-stream. */
async function encodeOctet(
  req: HttpRequest<unknown>,
): Promise<HttpRequest<unknown> | null> {
  const origContentType =
    req.headers.get("Content-Type") ??
    req.detectContentTypeHeader() ??
    "application/octet-stream";

  const serialized = req.serializeBody();
  let bytes: Uint8Array<ArrayBuffer>;
  if (typeof serialized === "string") {
    bytes = new TextEncoder().encode(serialized);
  } else if (serialized instanceof ArrayBuffer) {
    bytes = new Uint8Array(serialized);
  } else if (serialized instanceof Blob) {
    bytes = new Uint8Array(await serialized.arrayBuffer());
  } else {
    // URLSearchParams and friends serialise to a string above; anything left
    // (null) has no body to encode.
    return null;
  }

  // Nothing to encode (e.g. a bodiless DELETE) — forward unchanged.
  if (bytes.byteLength === 0) return null;

  const zip = canGzip();
  const encoded = await encodeBytes(bytes, zip);

  let headers = req.headers
    .set("X-Enc", "1")
    .set("X-Orig-Content-Type", origContentType)
    .set("Content-Type", "application/octet-stream");
  if (zip) headers = headers.set("X-Enc-Zip", "1");

  return req.clone({ body: encoded, headers });
}

/** multipart: encode text field values, keep files as binary, so uploads use
 *  the WAF's file allowance rather than the 128 KB body cap. */
async function encodeMultipart(
  req: HttpRequest<unknown>,
  form: FormData,
): Promise<HttpRequest<unknown>> {
  const zip = canGzip();
  const out = new FormData();
  for (const [key, value] of form.entries()) {
    if (typeof value === "string") {
      out.append(key, await encodeText(value, zip));
    } else if (value instanceof File) {
      out.append(key, value, value.name);
    } else {
      out.append(key, value); // non-File Blob: pass through as binary
    }
  }

  // Drop Content-Type so the browser generates a fresh multipart boundary for
  // the rebuilt FormData.
  let headers = req.headers.delete("Content-Type").set("X-Enc-Form", "1");
  if (zip) headers = headers.set("X-Enc-Zip", "1");

  return req.clone({ body: out, headers });
}

async function encode(
  req: HttpRequest<unknown>,
): Promise<HttpRequest<unknown>> {
  if (req.body instanceof FormData) {
    return encodeMultipart(req, req.body);
  }
  return (await encodeOctet(req)) ?? req;
}

export function wafEnvelopeInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  // Server-side rendering never crosses the WAF, and jsdom-less platforms have
  // neither btoa nor TextEncoder.
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return next(req);

  if (
    !MUTATING.has(req.method) ||
    hasEnvelope(req.headers) ||
    req.body == null ||
    !isSameOriginApiUrl(req.url)
  ) {
    return next(req);
  }

  return from(encode(req)).pipe(switchMap((encoded) => next(encoded)));
}
